import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import Page from 'src/components/page/Page';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import DocumentList from 'src/components/documents/DocumentList';
import useGenerateUploadUrl from 'src/api/core/documents/useGenerateUploadUrl';
import useConfirmUpload from 'src/api/core/documents/useConfirmUpload';
import useListDocuments, {
  ListDocumentsParams,
} from 'src/api/core/documents/useListDocuments';
import { uploadFileToStorage } from 'src/api/core/documents/uploadFileToStorage';
import {
  UploadProgress,
  UserDocument,
  DocumentStatus,
  UploadStatus,
} from 'src/types/UserDocuments';
import { useToast } from 'src/context/ToastContext';

const DocumentsPage: React.FC = () => {
  const { showToast } = useToast();

  // State
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters] = useState<ListDocumentsParams>({
    status: DocumentStatus.uploaded,
    limit: 25,
    offset: 0,
  });
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // API hooks
  const generateUploadUrl = useGenerateUploadUrl();
  const confirmUpload = useConfirmUpload(currentDocumentId || 'temp');

  // Calculate offset for pagination
  const paginationParams = {
    ...filters,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  };

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    error: documentsError,
    refetch: refetchDocuments,
  } = useListDocuments(paginationParams);

  // Upload flow implementation
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (isUploading) return;

      setIsUploading(true);

      for (const file of files) {
        const documentId = `temp-${Date.now()}-${Math.random()}`;

        // Add initial progress entry
        setUploadProgress((prev) => [
          ...prev,
          {
            documentId,
            fileName: file.name,
            progress: 0,
            status: UploadStatus.pending,
            file, // Store the file for retry
          },
        ]);

        try {
          // Step 1: Generate upload URL
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.documentId === documentId
                ? { ...p, status: UploadStatus.pending }
                : p,
            ),
          );

          const uploadUrlResponse = await generateUploadUrl.mutateAsync({
            fileName: file.name,
            contentType: file.type,
          });

          const { document, uploadUrl } = uploadUrlResponse.data;

          // Update with real document ID and set current document ID for the hook
          setCurrentDocumentId(document.id);
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.documentId === documentId
                ? {
                    ...p,
                    documentId: document.id,
                    status: UploadStatus.uploading,
                  }
                : p,
            ),
          );

          // Step 2: Upload file to storage with progress tracking
          await uploadFileToStorage(file, uploadUrl, (progress) => {
            setUploadProgress((prev) =>
              prev.map((p) =>
                p.documentId === document.id
                  ? { ...p, progress: Math.round(progress) }
                  : p,
              ),
            );
          });

          // Step 3: Confirm upload using useConfirmUpload hook
          await confirmUpload.mutateAsync({
            fileSizeBytes: file.size,
          });

          // Update to completed status
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.documentId === document.id
                ? { ...p, status: UploadStatus.uploaded, progress: 100 }
                : p,
            ),
          );

          showToast({
            message: `${file.name} uploaded successfully`,
            title: 'Upload Complete',
            color: 'success',
            icon: 'check',
          });
        } catch (error) {
          console.error('Upload failed:', error);

          // Extract user-friendly error message from new BaseError format
          let errorMessage = 'Upload failed';
          if (error instanceof Error) {
            errorMessage = error.message;
          } else if (typeof error === 'object' && error !== null) {
            // Handle BaseError format with user-friendly messages
            const baseError = error as { message?: string; error?: string };
            errorMessage =
              baseError.message || baseError.error || 'Upload failed';
          }

          // Update to failed status
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.documentId === documentId || p.fileName === file.name
                ? {
                    ...p,
                    status: UploadStatus.failed,
                    error: errorMessage,
                  }
                : p,
            ),
          );

          showToast({
            message: `Failed to upload ${file.name}: ${errorMessage}`,
            title: 'Upload Failed',
            color: 'error',
            icon: 'error',
          });
        }
      }

      setIsUploading(false);

      // Refresh document list after all uploads complete
      setTimeout(() => {
        refetchDocuments();
      }, 1000);
    },
    [
      isUploading,
      generateUploadUrl,
      showToast,
      refetchDocuments,
      confirmUpload,
    ],
  );

  // Document actions
  const handleDownload = useCallback(
    async (document: UserDocument) => {
      try {
        // Get document with signed URL
        const response = await fetch(`/api/core/documents/${document.id}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to get download URL');
        }

        const data = await response.json();
        const signedUrl = data.data.signedUrl;

        // Open in new tab for download
        window.open(signedUrl, '_blank');

        showToast({
          message: `Opening ${document.name}`,
          title: 'Download Started',
          color: 'info',
          icon: 'download',
        });
      } catch (error) {
        showToast({
          message: `Failed to download ${document.name}`,
          title: 'Download Failed',
          color: 'error',
          icon: 'error',
        });
      }
    },
    [showToast],
  );

  const handleDelete = useCallback(
    async (documentId: string) => {
      try {
        const response = await fetch(`/api/core/documents/${documentId}`, {
          method: 'DELETE',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to delete document');
        }

        // Refresh the list
        refetchDocuments();

        showToast({
          message: 'Document deleted successfully',
          title: 'Success',
          color: 'success',
          icon: 'check',
        });
      } catch (error) {
        showToast({
          message: 'Failed to delete document',
          title: 'Error',
          color: 'error',
          icon: 'error',
        });
      }
    },
    [refetchDocuments, showToast],
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  const removeUploadProgress = (documentId: string) => {
    setUploadProgress((prev) =>
      prev.filter((p) => p.documentId !== documentId),
    );
  };

  const handleRetryUpload = useCallback(
    async (documentId: string, fileName: string) => {
      // Find the failed upload
      const failedUpload = uploadProgress.find(
        (p) => p.documentId === documentId,
      );
      if (!failedUpload || !failedUpload.file) {
        showToast({
          message:
            'Original file not found. Please re-select and upload again.',
          title: 'Retry Failed',
          color: 'warning',
          icon: 'warning',
        });
        removeUploadProgress(documentId);
        return;
      }

      // Reset the upload status to pending and retry
      setUploadProgress((prev) =>
        prev.map((p) =>
          p.documentId === documentId
            ? {
                ...p,
                status: UploadStatus.pending as const,
                progress: 0,
                error: undefined,
              }
            : p,
        ),
      );

      // Retry the upload with the stored file
      try {
        setIsUploading(true);

        // Generate new upload URL (the old one might be expired)
        const uploadUrlResponse = await generateUploadUrl.mutateAsync({
          fileName: failedUpload.file.name,
          contentType: failedUpload.file.type,
        });

        const { document, uploadUrl } = uploadUrlResponse.data;

        // Update with new document ID and set current document ID for hook
        setCurrentDocumentId(document.id);
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.documentId === documentId
              ? {
                  ...p,
                  documentId: document.id,
                  status: UploadStatus.uploading,
                }
              : p,
          ),
        );

        // Upload file to storage with progress tracking
        await uploadFileToStorage(failedUpload.file, uploadUrl, (progress) => {
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.documentId === document.id
                ? { ...p, progress: Math.round(progress) }
                : p,
            ),
          );
        });

        // Confirm upload
        await confirmUpload.mutateAsync({
          fileSizeBytes: failedUpload.file.size,
        });

        // Update to completed status
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.documentId === document.id
              ? { ...p, status: UploadStatus.uploaded, progress: 100 }
              : p,
          ),
        );

        showToast({
          message: `${fileName} uploaded successfully on retry`,
          title: 'Retry Successful',
          color: 'success',
          icon: 'check',
        });

        // Refresh document list
        setTimeout(() => {
          refetchDocuments();
        }, 1000);
      } catch (error) {
        console.error('Retry upload failed:', error);

        // Extract user-friendly error message from new BaseError format
        let errorMessage = 'Retry failed';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          // Handle BaseError format with user-friendly messages
          const baseError = error as { message?: string; error?: string };
          errorMessage = baseError.message || baseError.error || 'Retry failed';
        }

        // Update to failed status again
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.documentId === documentId || p.fileName === fileName
              ? {
                  ...p,
                  status: UploadStatus.failed,
                  error: errorMessage,
                }
              : p,
          ),
        );

        showToast({
          message: `Retry failed for ${fileName}: ${errorMessage}`,
          title: 'Retry Failed',
          color: 'error',
          icon: 'error',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [
      uploadProgress,
      showToast,
      generateUploadUrl,
      refetchDocuments,
      confirmUpload,
    ],
  );

  const handleCancelUpload = useCallback((documentId: string) => {
    removeUploadProgress(documentId);
  }, []);

  return (
    <Page title="Documents">
      <MDBox>
        {/* Upload Section - Hero */}
        <Card
          sx={{
            mb: 4,
            backgroundColor: '#f8f9fa',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <MDBox textAlign="center" mb={3}>
              <MDTypography variant="body1" color="text">
                Drag and drop files or click to browse. All file types supported
                up to 20MB.
              </MDTypography>
            </MDBox>

            <FileUploadDropzone
              onFilesSelected={handleFilesSelected}
              uploadProgress={uploadProgress}
              isUploading={isUploading}
              onRetryUpload={handleRetryUpload}
              onCancelUpload={handleCancelUpload}
            />
          </CardContent>
        </Card>

        {/* Documents Section */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <MDBox p={3} pb={0}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={1}
              >
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium" mb={1}>
                    Your Documents
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    Manage and organize all your uploaded files
                  </MDTypography>
                </MDBox>

                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="grid">
                    <Icon>grid_view</Icon>
                  </ToggleButton>
                  <ToggleButton value="list">
                    <Icon>view_list</Icon>
                  </ToggleButton>
                </ToggleButtonGroup>
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 2 }} />

            <MDBox p={3} pt={0}>
              <DocumentList
                documents={documentsData?.results || []}
                isLoading={isLoadingDocuments}
                error={documentsError}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onRefresh={refetchDocuments}
                total={documentsData?.count || 0}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                viewMode={viewMode}
              />
            </MDBox>
          </CardContent>
        </Card>
      </MDBox>
    </Page>
  );
};

export default DocumentsPage;
